import { __extends } from "tslib";
import { MDCNotchedOutline } from '@material/notched-outline';
import { addClass, calculateFontWidth, getShadowElement, listenOwnProperty, Platform, removeClass } from '@tyler-components-web/core';
import { getActiveElement } from 'shadow-utils';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { FloatingLabel } from '../floating-label/floating-label';
import { TEXT_FIELD_CONSTANTS } from './text-field-constants';
import { toggleClass } from '../core';
var TextFieldAdapter = /** @class */ (function (_super) {
    __extends(TextFieldAdapter, _super);
    function TextFieldAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._valueChangeListeners = [];
        return _this;
    }
    TextFieldAdapter.prototype.initialize = function (required) {
        this._rootElement = getShadowElement(this._component, TEXT_FIELD_CONSTANTS.selectors.ROOT);
        this._labelSlot = getShadowElement(this._component, 'slot[name=label]');
        this._leadingSlot = getShadowElement(this._component, 'slot[name=leading]');
        this._trailingSlot = getShadowElement(this._component, 'slot[name=trailing]');
        this._addonEndSlot = getShadowElement(this._component, 'slot[name=addon-end]');
        this._inputElements = Array.from(this._component.querySelectorAll('input:not([type=checkbox]):not([type=radio]), textarea'));
        this._notchedOutlineElement = getShadowElement(this._component, TEXT_FIELD_CONSTANTS.selectors.NOTCHED_OUTLINE);
        this.detectLabel(required);
        if (this._inputElements.length > 1) {
            this._handleMultiInputs();
        }
    };
    TextFieldAdapter.prototype.destroy = function () {
        if (this._inputMutationObserver) {
            this._inputMutationObserver.disconnect();
        }
    };
    TextFieldAdapter.prototype.detectTextarea = function () {
        var _this = this;
        this._applyToInputs(function (input) {
            if (input instanceof HTMLTextAreaElement) {
                _this._rootElement.classList.add(TEXT_FIELD_CONSTANTS.classes.TEXTAREA);
            }
            else {
                _this._rootElement.classList.remove(TEXT_FIELD_CONSTANTS.classes.TEXTAREA);
            }
        });
    };
    TextFieldAdapter.prototype.hasLabel = function () {
        return !!this._labelElement;
    };
    TextFieldAdapter.prototype.ensureSlottedLabel = function () {
        this._labelElement.slot = 'label';
    };
    TextFieldAdapter.prototype.ensureLabelOrder = function () {
        if (this._labelElement) {
            var children = Array.from(this._component.children);
            if (children.length > 1 && children.indexOf(this._labelElement) < children.indexOf(this._inputElements[0])) {
                this._component.appendChild(this._labelElement);
            }
        }
    };
    TextFieldAdapter.prototype.addLabelSlotListener = function (listener) {
        this._labelSlot.addEventListener('slotchange', listener);
    };
    TextFieldAdapter.prototype.removeLabelSlotListener = function (listener) {
        if (this._labelSlot) {
            this._labelSlot.removeEventListener('slotchange', listener);
        }
    };
    TextFieldAdapter.prototype.addLeadingSlotListener = function (listener) {
        this._leadingSlot.addEventListener('slotchange', listener);
    };
    TextFieldAdapter.prototype.removeLeadingSlotListener = function (listener) {
        if (this._leadingSlot) {
            this._leadingSlot.removeEventListener('slotchange', listener);
        }
    };
    TextFieldAdapter.prototype.addTrailingSlotListener = function (listener) {
        this._trailingSlot.addEventListener('slotchange', listener);
    };
    TextFieldAdapter.prototype.removeTrailingSlotListener = function (listener) {
        if (this._trailingSlot) {
            this._trailingSlot.removeEventListener('slotchange', listener);
        }
    };
    TextFieldAdapter.prototype.addAddonEndSlotListener = function (listener) {
        this._addonEndSlot.addEventListener('slotchange', listener);
    };
    TextFieldAdapter.prototype.removeAddonEndSlotListener = function (listener) {
        if (this._addonEndSlot) {
            this._addonEndSlot.removeEventListener('slotchange', listener);
        }
    };
    TextFieldAdapter.prototype.addInputListener = function (type, listener) {
        this._applyToInputs(function (input) { return input.addEventListener(type, listener); });
    };
    TextFieldAdapter.prototype.removeInputListener = function (type, listener) {
        if (this._inputElements && this._inputElements.length > 0) {
            this._applyToInputs(function (input) { return input.removeEventListener(type, listener); });
        }
    };
    TextFieldAdapter.prototype.setValueChangedListener = function (context, listener) {
        var _this = this;
        this.destroyValueChangeListener();
        this._applyToInputs(function (input) {
            var destroyListener = listenOwnProperty(context, input, 'value', listener);
            _this._valueChangeListeners.push(destroyListener);
        });
    };
    TextFieldAdapter.prototype.destroyValueChangeListener = function () {
        this._valueChangeListeners.forEach(function (cb) { return cb(); });
    };
    TextFieldAdapter.prototype.detectLabel = function (required) {
        this._labelElement = this._component.querySelector('label');
        // Due to a Safari bug with ::slotted::after selectors, we need to manually append the required "asterisk" to the label text
        // https://bugs.webkit.org/show_bug.cgi?id=178237
        if (required && Platform.WEBKIT && this._labelElement && !this._labelElement.innerText.endsWith('*')) {
            var asterisk = document.createElement('span');
            asterisk.style.color = 'var(--mdc-theme-error)';
            asterisk.style.marginLeft = '1px';
            asterisk.textContent = '*';
            this._labelElement.appendChild(asterisk);
        }
    };
    TextFieldAdapter.prototype.initializeFloatingLabel = function () {
        return new FloatingLabel(this._labelElement);
    };
    TextFieldAdapter.prototype.initializeNotchedOutline = function () {
        return new MDCNotchedOutline(this._notchedOutlineElement);
    };
    TextFieldAdapter.prototype.hasValue = function () {
        return this._inputsSome(function (input) { return (input.value ? input.value.trim().length > 0 : false); });
    };
    TextFieldAdapter.prototype.hasPlaceholder = function () {
        return this._inputsSome(function (input) { return (input.placeholder ? input.placeholder.trim().length > 0 : false); });
    };
    TextFieldAdapter.prototype.hasFocus = function () {
        return this._inputsSome(function (input) { return input === getActiveElement(); });
    };
    TextFieldAdapter.prototype.setLabelClass = function (name) {
        if (this._labelElement) {
            this._labelElement.classList.add(name);
        }
    };
    TextFieldAdapter.prototype.removeLabelClass = function (name) {
        if (this._labelElement) {
            this._labelElement.classList.remove(name);
        }
    };
    TextFieldAdapter.prototype.setRoomy = function (isRoomy) {
        if (isRoomy) {
            this._rootElement.classList.add(TEXT_FIELD_CONSTANTS.classes.ROOMY);
        }
        else {
            this._rootElement.classList.remove(TEXT_FIELD_CONSTANTS.classes.ROOMY);
        }
    };
    TextFieldAdapter.prototype.setDense = function (isDense) {
        if (isDense) {
            this._rootElement.classList.add(TEXT_FIELD_CONSTANTS.classes.DENSE);
        }
        else {
            this._rootElement.classList.remove(TEXT_FIELD_CONSTANTS.classes.DENSE);
        }
    };
    TextFieldAdapter.prototype.setInset = function (isInset) {
        toggleClass(this._rootElement, isInset, TEXT_FIELD_CONSTANTS.classes.INSET);
    };
    TextFieldAdapter.prototype.hasLeadingNodes = function () {
        if (!this._leadingSlot) {
            return false;
        }
        return this._leadingSlot.assignedNodes().length > 0;
    };
    TextFieldAdapter.prototype.hasTrailingNodes = function () {
        if (!this._trailingSlot) {
            return false;
        }
        return this._trailingSlot.assignedNodes().length > 0;
    };
    TextFieldAdapter.prototype.hasAddonEndNodes = function () {
        if (!this._addonEndSlot) {
            return false;
        }
        return this._addonEndSlot.assignedNodes().length > 0;
    };
    TextFieldAdapter.prototype.addClass = function (classes) {
        addClass(classes, this._rootElement);
    };
    TextFieldAdapter.prototype.removeClass = function (classes) {
        removeClass(classes, this._rootElement);
    };
    TextFieldAdapter.prototype.setInputClass = function (className) {
        this._applyToInputs(function (input) { return input.classList.add(className); });
    };
    TextFieldAdapter.prototype.removeInputClass = function (className) {
        this._applyToInputs(function (input) { return input.classList.remove(className); });
    };
    TextFieldAdapter.prototype.setRootClass = function (classes) {
        addClass(classes, this._rootElement);
    };
    TextFieldAdapter.prototype.removeRootClass = function (classes) {
        removeClass(classes, this._rootElement);
    };
    TextFieldAdapter.prototype.setInputAttributeObserver = function (listener) {
        var _this = this;
        this._applyToInputs(function (input) {
            _this._inputMutationObserver = new MutationObserver(function (mutations) {
                for (var _i = 0, mutations_1 = mutations; _i < mutations_1.length; _i++) {
                    var mutation = mutations_1[_i];
                    if (mutation.attributeName) {
                        listener(mutation.attributeName, input.getAttribute(mutation.attributeName));
                    }
                }
            });
            _this._inputMutationObserver.observe(input, { attributes: true, attributeFilter: ['disabled', 'readonly', 'value'] });
        });
    };
    TextFieldAdapter.prototype.isDisabled = function () {
        return this._inputsSome(function (input) { return input.hasAttribute('disabled'); });
    };
    TextFieldAdapter.prototype.isReadonly = function () {
        return this._inputsSome(function (input) { return input.hasAttribute('readonly'); });
    };
    TextFieldAdapter.prototype.getLabelWidth = function (fontSize, fontFamily) {
        return calculateFontWidth(this._labelElement.innerText, { fontSize: fontSize, fontFamily: fontFamily });
    };
    TextFieldAdapter.prototype.getLabelFontMetrics = function () {
        var style = getComputedStyle(this._labelElement);
        return {
            fontSize: parseInt(style.fontSize || '16', 10),
            fontFamily: style.fontFamily || 'Roboto'
        };
    };
    TextFieldAdapter.prototype._inputsSome = function (action) {
        return this._inputElements.some(action);
    };
    TextFieldAdapter.prototype._applyToInputs = function (action) {
        return this._inputElements.forEach(action);
    };
    TextFieldAdapter.prototype._handleMultiInputs = function () {
        var _this = this;
        addClass(TEXT_FIELD_CONSTANTS.classes.MULTI_INPUT, this._rootElement);
        this._applyToInputs(function (input, index) {
            input.setAttribute(TEXT_FIELD_CONSTANTS.attributes.MULTI_INPUT + "-" + index.toString(), '');
            if (index % 2 !== 1) {
                Promise.resolve().then(function () { return input.insertAdjacentElement('afterend', _this._createSeperatorElement()); });
            }
        });
    };
    TextFieldAdapter.prototype._createSeperatorElement = function () {
        var divider = document.createElement('span');
        divider.style.margin = '0 8px 0 4px';
        divider.style.color = 'var(--mdc-theme-text-primary-on-background)';
        divider.style.border = 'transparent';
        divider.textContent = '-';
        return divider;
    };
    return TextFieldAdapter;
}(BaseAdapter));
export { TextFieldAdapter };
